import React, {
	useState,
	useEffect,
	useRef,
	useCallback,
	useContext,
	useMemo,
} from "react";
import {
	Send,
	Paperclip,
	Users,
	Loader2,
	X,
	FileImage,
	FileVideo,
	FileAudio,
	FileText,
	FileArchive,
	FileSpreadsheet,
	Image as ImageIcon,
	Download,
	Eye,
	Smile,
	Mic,
	Search,
	ChevronUp,
	ChevronDown,
	Sparkles,
	SpellCheck,
	MessageSquare,
	Check,
	Sticker,
} from "lucide-react";
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";

import FigurinhaPicker from "../FigurinhaPicker";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import RecordingTimer from "../MessageInput/RecordingTimer";
import { paraWav } from "../MessageInput/wav";
import VisualizadorDeArquivo from "../VisualizadorDeArquivo";
import MiniaturaDePdf from "../MiniaturaDePdf";
import { extensaoDe } from "../../helpers/tiposDeArquivo";
import { cn } from "../../lib/utils";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useThemeContext } from "../../context/DarkMode";
import { getPublicFileUrl } from "../../config";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import {
	tocarSomNovoChatInterno,
	tocarSomEnviadoChatInterno,
} from "../../services/chatInternoSom";
import { getInitials, corPontoPresenca, rotuloPresenca } from "../InternalChatWidget/shared.jsx";
import { touchInternalChat } from "../../services/recentContext";

const WHATSAPP_LIGHT = {
	bgChat: "#efeae2",
	bgChatDots:
		"url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'><g fill='%23000000' fill-opacity='0.028'><circle cx='120' cy='120' r='1.5'/><circle cx='280' cy='80' r='1.5'/><circle cx='360' cy='200' r='1.5'/><circle cx='60' cy='300' r='1.5'/><circle cx='200' cy='360' r='1.5'/><circle cx='320' cy='320' r='1.5'/></g></svg>\")",
	balaoEnviada: "#d9fdd3",
	balaoRecebida: "#ffffff",
	corTextoEnviada: "#111b21",
	corTextoRecebida: "#111b21",
	corTimeEnviada: "#667781",
	corTimeRecebida: "#667781",
	bgHeader: "#f0f2f5",
	bgInput: "#f0f2f5",
	bgChipReaction: "rgba(255,255,255,0.8)",
	bgChipReactionMinha: "rgba(255,255,255,0.9)",
	bgChipReactionEu: "#e8f4ee",
	bgChipReactionEuMinha: "#e9f3f0",
	textoMuted: "#667781",
	borderSeparator: "#e9edef",
	avatarBgGrupo: "#d1f4cc",
	avatarBgPessoa: "#e3f0da",
	avatarIconColor: "#008069",
	chipAnexoBg: "#ffffff",
	inputBg: "#ffffff",
};

const WHATSAPP_DARK = {
	bgChat: "#0b141a",
	bgChatDots:
		"url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'><g fill='%23ffffff' fill-opacity='0.04'><circle cx='120' cy='120' r='1.5'/><circle cx='280' cy='80' r='1.5'/><circle cx='360' cy='200' r='1.5'/><circle cx='60' cy='300' r='1.5'/><circle cx='200' cy='360' r='1.5'/><circle cx='320' cy='320' r='1.5'/></g></svg>\")",
	balaoEnviada: "#005c4b",
	balaoRecebida: "#202c33",
	corTextoEnviada: "#e9edef",
	corTextoRecebida: "#e9edef",
	corTimeEnviada: "#8696a0",
	corTimeRecebida: "#8696a0",
	bgHeader: "#202c33",
	bgInput: "#2a3942",
	bgChipReaction: "rgba(32,44,51,0.92)",
	bgChipReactionMinha: "rgba(0,92,75,0.92)",
	bgChipReactionEu: "#17423b",
	bgChipReactionEuMinha: "#0b624f",
	textoMuted: "#8696a0",
	borderSeparator: "#233138",
	avatarBgGrupo: "#175c49",
	avatarBgPessoa: "#2c3d37",
	avatarIconColor: "#00cf8c",
	chipAnexoBg: "#2a3942",
	inputBg: "#2a3942",
};

const FORMATOS_AUDIO = [
	{ mime: "audio/ogg;codecs=opus", ext: "ogg" },
	{ mime: "audio/webm;codecs=opus", ext: "webm" },
	{ mime: "audio/mp4", ext: "m4a" },
	{ mime: "audio/webm", ext: "webm" },
];

const escolherFormatoAudio = () => {
	if (typeof MediaRecorder === "undefined") return null;
	return FORMATOS_AUDIO.find((f) => MediaRecorder.isTypeSupported(f.mime)) || {
		mime: "",
		ext: "webm",
	};
};

const PLANILHAS = [".xls", ".xlsx", ".csv", ".ods"];
const COMPACTADOS = [".zip", ".rar", ".7z", ".gz", ".tgz"];

const iconeDoAnexo = (tipo, className = "h-5 w-5") => {
	switch (tipo) {
		case "image":
			return <ImageIcon className={className} />;
		case "video":
			return <FileVideo className={className} />;
		case "audio":
			return <FileAudio className={className} />;
		case "application":
		default:
			return <FileText className={className} />;
	}
};

const IconeDoDocumento = ({ extensao }) => {
	if (PLANILHAS.includes(extensao))
		return <FileSpreadsheet className="h-8 w-8 shrink-0 text-muted-foreground" />;
	if (COMPACTADOS.includes(extensao))
		return <FileArchive className="h-8 w-8 shrink-0 text-muted-foreground" />;
	return <FileText className="h-8 w-8 shrink-0 text-muted-foreground" />;
};

const tipoDeMidia = (nomeOuMime) => {
	if (!nomeOuMime) return "application";
	const mime = typeof nomeOuMime === "string" && nomeOuMime.includes("/") ? nomeOuMime : "";
	const ext = mime || extensaoDe(String(nomeOuMime || "")).replace(/^\./, "");
	if (mime.startsWith("image/") || /jpe?g|png|gif|webp|svg|bmp/.test(ext)) return "image";
	if (mime.startsWith("video/") || /mp4|webm|mov|mkv/.test(ext)) return "video";
	if (mime.startsWith("audio/") || /mp3|wav|ogg|opus|m4a/.test(ext)) return "audio";
	return "application";
};

// Parsing: "internal-direct-7" -> { type: "direct", id: 7 }
//          "internal-group-3"  -> { type: "group",  id: 3 }
const parseChatKey = (raw) => {
	if (!raw || typeof raw !== "string") return null;
	const m = raw.match(/^internal-(direct|group)-(\d+)$/);
	if (!m) return null;
	return { type: m[1], id: Number(m[2]) };
};

const RenderizarMidia = ({ mensagem, tema, darkMode, onAbrirImagem, abrirArquivo }) => {
	if (!mensagem?.mediaUrl) return null;
	const url = getPublicFileUrl(mensagem.mediaUrl);
	const tipo = mensagem.mediaType || tipoDeMidia(mensagem.mediaUrl);
	const nome = decodeURIComponent(String(mensagem.mediaUrl || "").split("/").pop() || "Arquivo");
	const ext = extensaoDe(mensagem.mediaUrl).replace(".", "").toUpperCase() || "ARQUIVO";

	if (tipo === "sticker") {
		// IGUAL ao MessagesList/index.jsx de tickets, InternalChatWidget e
		// pages/InternalChat: 150x150px fixo, sem botão de zoom, draggable.
		// Obs: InternalChatPanel (painel lateral DENTRO do TicketManager) NÃO
		// tinha este case — figurinhas enviadas OUTROS componentes (widget
		// popup ou página /internal-chat) chegavam com mediaType="sticker" e
		// este painel, sem o case, caía no bloco "Arquivo WEBP" genérico,
		// que era exatamente o bug relatado pelo usuário (quem recebe via
		// TicketManager via "Arquivo WEBP" em vez de figurinha 150x150).
		if (!url) {
			return (
				<div
					className="mb-1 flex h-[150px] w-[150px] items-center justify-center rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/20 text-[10px] uppercase tracking-wider text-muted-foreground"
					title="Figurinha não carregada"
				>
					Figurinha
				</div>
			);
		}
		return (
			<div className="mb-1" style={{ background: "transparent" }}>
				<img
					src={url}
					alt={nome}
					loading="eager"
					decoding="async"
					draggable
					onContextMenu={(e) => {
						try { e.stopPropagation(); } catch {}
					}}
					style={{ WebkitUserDrag: "auto", userSelect: "auto" }}
					className="h-[150px] w-[150px] object-contain"
				/>
			</div>
		);
	}

	if (tipo === "image") {
		return (
			<button
				type="button"
				onClick={(e) => { e.preventDefault(); onAbrirImagem?.(url, nome); }}
				className="mb-1 block w-full overflow-hidden rounded-lg text-left"
				style={{ background: "transparent", border: 0, padding: 0, cursor: "zoom-in" }}
			>
				<img src={url} alt={nome} loading="lazy" className="max-h-60 w-full rounded-lg object-cover" />
			</button>
		);
	}
	if (tipo === "video") {
		return <video src={url} controls preload="metadata" className="mb-1 max-h-64 w-full rounded-lg bg-black" />;
	}
	if (tipo === "audio") {
		return (
			<div className="mb-1">
				<div className="flex w-full items-center gap-2 rounded-lg p-2"
					style={{ backgroundColor: darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)" }}>
					<FileAudio className="h-5 w-5 shrink-0" style={{ color: tema.textoMuted }} />
					<audio src={url} controls preload="metadata" className="flex-1 min-w-0" />
				</div>
			</div>
		);
	}
	const urlMedia = url;
	const extensaoLower = extensaoDe(mensagem.mediaUrl);
	const rotulo = extensaoLower ? `Arquivo ${extensaoLower.replace(".", "").toUpperCase()}` : "Arquivo";
	return (
		<div className="mb-1 rounded-lg border bg-black/[0.03] p-2 dark:bg-white/[0.04]">
			{extensaoLower === ".pdf" && (
				<div className="mb-2">
					<MiniaturaDePdf url={urlMedia} onAbrir={() => abrirArquivo?.(mensagem.id)} />
				</div>
			)}
			<div className="flex items-center gap-2">
				<IconeDoDocumento extensao={extensaoLower} />
				<div className="min-w-0 flex-1">
					<p className="truncate text-sm font-medium">{rotulo}</p>
					<p className="text-xs text-muted-foreground">{ext ? ext : "documento"}</p>
				</div>
				<Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" title="Visualizar" onClick={() => abrirArquivo?.(mensagem.id)}>
					<Eye className="h-4 w-4" />
				</Button>
				<Button asChild variant="ghost" size="icon" className="h-8 w-8 shrink-0" title="Baixar">
					<a href={urlMedia} target="_blank" rel="noopener noreferrer">
						<Download className="h-4 w-4" />
					</a>
				</Button>
			</div>
		</div>
	);
};

const EMOJIS_RAPIDOS = ["👍","❤️","😂","😮","😢","🙏","🔥","🎉"];

const InternalChatPanel = ({ chatKey }) => {
	const { darkMode } = useThemeContext();
	const tema = darkMode ? WHATSAPP_DARK : WHATSAPP_LIGHT;
	const { user } = useContext(AuthContext);

	const parsed = useMemo(() => parseChatKey(chatKey), [chatKey]);
	const type = parsed?.type; // "direct" | "group"
	const targetId = parsed?.id;

	const [conversaInfo, setConversaInfo] = useState(null); // { name, profilePicUrl, status, unreadCount? }
	const [mensagens, setMensagens] = useState([]);
	const [carregandoMsgs, setCarregandoMsgs] = useState(true);
	const [erro, setErro] = useState(null);
	const [texto, setTexto] = useState("");
	const [anexos, setAnexos] = useState([]);
	const [enviando, setEnviando] = useState(false);
	const [gravando, setGravando] = useState(false);

	// (E) busca
	const [buscaAberta, setBuscaAberta] = useState(false);
	const [buscaMsg, setBuscaMsg] = useState("");
	const [indiceMatch, setIndiceMatch] = useState(0);

	// Reações
	const [reactionPopoverFor, setReactionPopoverFor] = useState(null);

	// (F) Figurinhas (igual InternalChatWidget popup header)
	const [figurinhaAberto, setFigurinhaAberto] = useState(false);
	const figurinhaAncoraRef = useRef(null);

	// IA Copiloto
	const [aiAberto, setAiAberto] = useState(false);
	const [aiModo, setAiModo] = useState(null);
	const [aiCarregando, setAiCarregando] = useState("");
	const [aiErro, setAiErro] = useState("");
	const [aiVersoes, setAiVersoes] = useState([]);
	const [aiSugestoes, setAiSugestoes] = useState([]);
	const TONS_IA = [
		{ acao: "corrigir", rotulo: "Corrigido" },
		{ acao: "melhorar", rotulo: "Mais claro" },
		{ acao: "formal", rotulo: "Mais formal" },
		{ acao: "informal", rotulo: "Mais informal" },
		{ acao: "resumir", rotulo: "Mais curto" },
	];

	// Visualizadores
	const [imagemModal, setImagemModal] = useState(null); // { url, nome }
	const [visualizando, setVisualizando] = useState(null);

	const inputRef = useRef(null);
	const fileRef = useRef(null);
	const msgsEndRef = useRef(null);
	const msgsScrollRef = useRef(null);
	const gravadorRef = useRef(null);
	const pedacosRef = useRef([]);
	const socketRef = useRef(null);
	const userRef = useRef(null);

	const arquivosDaConversa = useMemo(() => {
		return mensagens
			.filter((m) => m.mediaUrl && !m.isDeleted)
			.map((m) => ({
				id: m.id,
				url: getPublicFileUrl(m.mediaUrl),
				mediaType: m.mediaType,
				legenda: m.body || "",
				fromMe: m.fromMe,
				createdAt: m.createdAt,
			}));
	}, [mensagens]);

	const abrirArquivo = (messageId) => {
		const indice = arquivosDaConversa.findIndex((a) => a.id === messageId);
		if (indice >= 0) setVisualizando(indice);
	};

	useEffect(() => { userRef.current = user; }, [user]);

	// Scroll para o fim com múltiplas tentativas (igual widget)
	const scrollParaFim = useCallback(({ suave = false } = {}) => {
		const el = msgsScrollRef.current;
		if (!el) return;
		const ir = () => { try { el.scrollTop = el.scrollHeight + 99999; } catch {} };
		queueMicrotask(ir);
		requestAnimationFrame(() => { ir(); requestAnimationFrame(ir); });
		const delays = suave ? [20, 50, 100, 180, 320] : [10, 40, 90, 160];
		delays.forEach((d) => setTimeout(ir, d));
	}, []);

	const scrollParaMsgPorId = useCallback((msgId) => {
		const container = msgsScrollRef.current;
		if (!container || msgId == null) return;
		const rolar = () => {
			try {
				const li = container.querySelector(`[data-msg-id="${msgId}"]`);
				if (!li) return;
				const rectContainer = container.getBoundingClientRect();
				const rectLi = li.getBoundingClientRect();
				const relTop = rectLi.top - rectContainer.top;
				const desejado = container.scrollTop + relTop - (container.clientHeight / 2) + (rectLi.height / 2);
				container.scrollTo({ top: Math.max(0, desejado), behavior: "smooth" });
			} catch {}
		};
		queueMicrotask(rolar);
		requestAnimationFrame(() => { rolar(); requestAnimationFrame(rolar); });
		setTimeout(rolar, 60);
	}, []);

	const escapeRegex = (s) => String(s || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

	const matches = useMemo(() => {
		const q = String(buscaMsg || "").trim();
		if (!q) return [];
		const re = new RegExp(escapeRegex(q), "i");
		const result = [];
		mensagens.forEach((m, msgIdx) => {
			const campos = [
				String(m.body || ""),
				String(m.mediaUrl || ""),
				decodeURIComponent(String(m.mediaUrl || "").split("/").pop() || ""),
			];
			if (campos.some((c) => re.test(c))) result.push({ msgIdx, id: m.id });
		});
		return result;
	}, [mensagens, buscaMsg]);

	useEffect(() => {
		if (matches.length > 0 && buscaAberta) {
			scrollParaMsgPorId(matches[0].id);
		}
	}, [buscaMsg, buscaAberta, matches, scrollParaMsgPorId]);

	// Carregar conversa + histórico SEMPRE que chatKey mudar
	useEffect(() => {
		let ativo = true;
		setCarregandoMsgs(true);
		setErro(null);
		setMensagens([]);
		setConversaInfo(null);
		setTexto("");
		setAnexos([]);

		if (!parsed || !type || !targetId) {
			setCarregandoMsgs(false);
			setErro("Conversa inválida.");
			return;
		}

		(async () => {
			try {
				// 1) pegar lista de conversas para pegar nome/foto da conversa alvo
				const convResp = await api.get("/internal-chat/conversations").catch(() => ({ data: [] }));
				const todas = Array.isArray(convResp.data) ? convResp.data : [];
				const alvo = todas.find((c) => {
					if (c.type !== type) return false;
					const cid = c.type === "group" ? c.groupId : c.userId;
					return cid === targetId;
				});
				if (ativo && alvo) {
					setConversaInfo({
						name: alvo.name,
						profilePicUrl: alvo.profilePicUrl,
						type: alvo.type,
						groupId: alvo.groupId,
						userId: alvo.userId,
					});
				} else if (ativo) {
					// fallback: se não achar na lista, usa dados mínimos
					setConversaInfo({ name: type === "group" ? "Grupo" : "Usuário", type, groupId: type==="group"?targetId:null, userId: type==="direct"?targetId:null });
				}

				// 2) carregar mensagens
				let msgs = [];
				if (type === "direct") {
					const { data } = await api.get(`/internal-chat/direct/${targetId}/messages`, { params: { pageSize: 80 } });
					msgs = Array.isArray(data?.messages) ? data.messages : [];
				} else if (type === "group") {
					const { data } = await api.get(`/internal-chat/group/${targetId}/messages`, { params: { pageSize: 80 } }).catch(() => ({ data: { messages: [] } }));
					msgs = Array.isArray(data?.messages) ? data.messages : [];
				}
				if (!ativo) return;
				setMensagens(msgs);

				if (alvo) {
					touchInternalChat({
						id: type === "group" ? alvo.groupId : alvo.userId,
						name: alvo.name,
						lastMessage: Array.isArray(msgs) && msgs.length ? (msgs[msgs.length - 1]?.body || "") : null,
					});
				}

				// 3) marcar como lido
				try {
					if (type === "direct") {
						await api.post(`/internal-chat/${targetId}/read?type=direct`).catch(() => {});
					} else if (type === "group") {
						await api.post(`/internal-chat/${targetId}/read?type=group`).catch(() => {});
					}
				} catch {}
			} catch (err) {
				if (ativo) {
					setErro(err?.message || "Erro ao carregar conversa.");
					toastError?.(err);
				}
			} finally {
				if (ativo) {
					setCarregandoMsgs(false);
					requestAnimationFrame(() => {
						inputRef.current?.focus?.();
						scrollParaFim({ suave: true });
					});
				}
			}
		})();

		// Socket: ouvir mensagens novas desta conversa
		const socket = openSocket();
		socketRef.current = socket;

		const onMsg = ({ message }) => {
			if (!message) return;
			const eu = userRef.current;
			const fromMe = Number(message.fromUserId) === Number(eu?.id);

			let ehEssaConversa = false;
			if (type === "direct") {
				ehEssaConversa =
					(Number(message.fromUserId) === targetId && Number(message.toUserId) === Number(eu?.id)) ||
					(Number(message.toUserId) === targetId && fromMe);
			} else if (type === "group") {
				ehEssaConversa = Number(message.groupId) === targetId;
			}
			if (!ehEssaConversa) return;

			setMensagens((prev) => {
				if (prev.find((x) =>
					(message.id && x.id === message.id) ||
					(message.__tempId && x.__tempId === message.__tempId)
				)) return prev;
				return [...prev, message];
			});

			if (!fromMe) tocarSomNovoChatInterno();
		};

		const onReaction = (payload) => {
			if (!payload || typeof payload.messageId !== "number") return;
			setMensagens((prev) => {
				let mudou = false;
				const novas = prev.map((m) => {
					if (+m.id !== +payload.messageId) return m;
					mudou = true;
					return { ...m, reacoes: Array.isArray(payload.reacoes) ? payload.reacoes : [] };
				});
				return mudou ? novas : prev;
			});
		};

		const onFavorite = (payload) => {
			if (!payload || typeof payload.messageId !== "number") return;
			setMensagens((prev) => {
				let mudou = false;
				const novas = prev.map((m) => {
					if (+m.id !== +payload.messageId) return m;
					mudou = true;
					return { ...m, favorita: !!payload.favorita };
				});
				return mudou ? novas : prev;
			});
		};

		const onMessageEdit = (payload) => {
			if (!payload || typeof payload.messageId !== "number") return;
			setMensagens((prev) => {
				let mudou = false;
				const novas = prev.map((m) => {
					if (+m.id !== +payload.messageId) return m;
					mudou = true;
					return {
						...m,
						body: typeof payload.body === "string" ? payload.body : m.body,
						editedAt: payload.editedAt || m.editedAt || new Date().toISOString(),
					};
				});
				return mudou ? novas : prev;
			});
		};

		const onPresence = ({ userId: uid, status }) => {
			if (type !== "direct") return;
			if (Number(uid) !== targetId) return;
			setConversaInfo((prev) => prev ? { ...prev, status } : prev);
		};

		socket.on("internalChat:message", onMsg);
		socket.on("internalChat:reaction", onReaction);
		socket.on("internalChat:favorite", onFavorite);
		socket.on("internalChat:messageEdit", onMessageEdit);
		socket.on("userPresenceChange", onPresence);

		// ── Conversation update (qualquer fluxo que toque em unread / última
		//    mensagem: markAsRead de outra aba, envio paralelo etc.)
		// Recarrega a lista DESTA conversa para atualizar unreadCount no
		// cabeçalho (badge de não lidas desaparecer com o próprio atendente
		// marca lida no seu celular, a outra aba reflete SEM F5).
		const onConversationUpdate = () => {
			if (!ativo) return;
			api
				.get("/internal-chat/conversations")
				.then(({ data }) => {
					if (!ativo || !parsed || !type || !targetId) return;
					const convs = Array.isArray(data) ? data : [];
					const match = convs.find(
						c =>
						(type === "group" && +c.groupId === +targetId) ||
						(type === "direct" && +c.userId === +targetId)
					);
					if (match) {
						setConversaInfo(prev => prev
							? {
								...prev,
								unreadCount: match.unreadCount || 0,
								name: match.name || prev.name,
								profilePicUrl: typeof match.profilePicUrl === "string"
									? match.profilePicUrl
									: prev.profilePicUrl,
							}
							: prev
						);
					}
				})
				.catch(() => undefined);
		};
		socket.on("internalChat:conversationUpdate", onConversationUpdate);

		return () => {
			ativo = false;
			// Limpa APENAS os listeners DESTA view.
			// socket.disconnect() NÃO PODE mais: conexão singleton compartilhada
			// com a fila de tickets, transferências, visto, presença etc.
			try { socket.off("internalChat:message", onMsg); } catch (_) {}
			try { socket.off("internalChat:reaction", onReaction); } catch (_) {}
			try { socket.off("internalChat:favorite", onFavorite); } catch (_) {}
			try { socket.off("internalChat:messageEdit", onMessageEdit); } catch (_) {}
			try { socket.off("userPresenceChange", onPresence); } catch (_) {}
			try { socket.off("internalChat:conversationUpdate", onConversationUpdate); } catch (_) {}
			socketRef.current = null;
		};
	}, [parsed, type, targetId, scrollParaFim]);

	// Auto-scroll quando mensagens mudam
	useEffect(() => {
		if (!mensagens.length) return;
		scrollParaFim();
	}, [mensagens.length, scrollParaFim]);

	// Ações anexo / gravação / envio
	const adicionarAnexos = (arquivos) => {
		const arr = Array.from(arquivos || []);
		if (!arr.length) return;
		setAnexos((anteriores) => [
			...anteriores,
			...arr.map((arq) => {
				const ehImagem = arq.type?.startsWith("image/") ||
					/^image/i.test(tipoDeMidia(arq.type || arq.name));
				const preview = ehImagem ? URL.createObjectURL(arq) : null;
				return { file: arq, preview };
			}),
		]);
	};

	const removerAnexo = (index) => {
		setAnexos((antes) => {
			const prox = [...antes];
			const [removido] = prox.splice(index, 1);
			if (removido?.preview) try { URL.revokeObjectURL(removido.preview); } catch {}
			return prox;
		});
	};

	const handlePaste = (e) => {
		if (!e.clipboardData) return;
		let arquivosColados = [];
		try { arquivosColados = Array.from(e.clipboardData.files || []); } catch {}
		if (arquivosColados.length === 0 && e.clipboardData.items) {
			const arr = [];
			for (let i = 0; i < e.clipboardData.items.length; i++) {
				const it = e.clipboardData.items[i];
				if (it && it.kind === "file") {
					const arq = it.getAsFile && it.getAsFile();
					if (arq) arr.push(arq);
				}
			}
			arquivosColados = arr;
		}
		if (!arquivosColados.length) return;
		e.preventDefault();
		adicionarAnexos(arquivosColados);
	};

	const handleStartRecord = async () => {
		if (gravando) return;
		const formato = escolherFormatoAudio();
		if (!formato) { toast.error("Este navegador não suporta gravação de áudio."); return; }
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			const gravador = formato.mime ? new MediaRecorder(stream, { mimeType: formato.mime }) : new MediaRecorder(stream);
			pedacosRef.current = [];
			gravadorRef.current = { recorder: gravador, stream, ext: formato.ext };
			gravador.ondataavailable = (e) => { if (e.data && e.data.size > 0) pedacosRef.current.push(e.data); };
			gravador.start(1000);
			setGravando(true);
		} catch (err) {
			if (err?.name === "NotAllowedError") toast.error("Você bloqueou o microfone.");
			else if (err?.name === "NotFoundError") toast.error("Nenhum microfone encontrado.");
			else toastError?.(err);
		}
	};

	const encerrarGravacao = () => new Promise((resolve) => {
		const atual = gravadorRef.current;
		if (!atual) { resolve(null); return; }
		const finalizar = () => {
			atual.stream.getTracks().forEach((t) => t.stop());
			gravadorRef.current = null;
			const blob = new Blob(pedacosRef.current, { type: atual.recorder.mimeType || "audio/webm" });
			pedacosRef.current = [];
			resolve({ blob, ext: atual.ext });
		};
		if (atual.recorder.state === "inactive") { finalizar(); return; }
		atual.recorder.onstop = finalizar;
		atual.recorder.stop();
	});

	const handleUploadAudio = async () => {
		try {
			const gravado = await encerrarGravacao();
			setGravando(false);
			if (!gravado) return;
			if (gravado.blob.size < 2000) { toast.info("Gravação muito curta."); return; }
			const wav = await paraWav(gravado.blob);
			const nome = `${new Date().getTime()}.wav`;
			const arquivo = new File([wav], nome, { type: "audio/wav" });
			adicionarAnexos([arquivo]);
			await new Promise((r) => setTimeout(r, 120));
			enviarMensagem();
		} catch (err) {
			console.error(err);
			toastError?.(err);
			setGravando(false);
		}
	};

	const handleCancelAudio = async () => { await encerrarGravacao(); setGravando(false); };

	const enviarFigurinhaChatInterno = useCallback(
		async (figurinha, ctx) => {
			if (!type || !targetId || !figurinha?.id) return false;
			try {
				setEnviando(true);
				const ehGrupo = type === "group";
				const rota = ehGrupo
					? `/internal-chat/groups/${targetId}/figurinhas/${figurinha.id}/enviar`
					: `/internal-chat/direct/${targetId}/figurinhas/${figurinha.id}/enviar`;

				const tempId = `tmp_panel_sticker_${Date.now()}`;
				const temporaria = {
					__tempId: tempId,
					id: -Math.floor(Math.random() * 1e9),
					body: "",
					mediaUrl: getPublicFileUrl(figurinha.mediaUrl) || "",
					mediaType: "sticker",
					fromUserId: Number(user?.id),
					toUserId: ehGrupo ? null : Number(targetId),
					groupId: ehGrupo ? Number(targetId) : null,
					createdAt: new Date().toISOString(),
					fromUser: {
						id: Number(user?.id),
						name: user?.name || "Eu",
						profilePicUrl: user?.profilePicUrl || null,
					},
				};
				setMensagens((m) => [...m, temporaria]);
				tocarSomEnviadoChatInterno();
				requestAnimationFrame(() => {
					msgsEndRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
				});

				const payload = {};
				const quotedMsg = ctx?.quotedMsg || null;
				if (quotedMsg && quotedMsg.id) payload.quotedMsgId = String(quotedMsg.id);

				const { data } = await api.post(rota, payload);
				const enviadas = Array.isArray(data) ? data : [data];
				setMensagens((m) => {
					const semTemp = m.filter((x) => x.__tempId !== tempId);
					return [...semTemp, ...enviadas];
				});
				setFigurinhaAberto(false);
				return true;
			} catch (err) {
				toastError?.(err);
				return false;
			} finally {
				setEnviando(false);
			}
		},
		[type, targetId, user]
	);

	const enviarMensagem = async () => {
		if (!parsed || !type || !targetId) return;
		const corpo = texto.trim();
		if (!corpo && anexos.length === 0) return;
		setEnviando(true);
		try {
			const form = new FormData();
			if (corpo) form.append("body", corpo);
			anexos.forEach((a) => form.append("medias", a.file, a.file.name));

			const tempId = `tmp_panel_${Date.now()}`;
			const temporaria = {
				__tempId: tempId,
				id: -Math.floor(Math.random() * 1e9),
				body: corpo || null,
				mediaUrl: anexos[0]?.file?.name || null,
				mediaType: anexos[0]?.file?.type || null,
				fromUserId: Number(user?.id),
				createdAt: new Date().toISOString(),
				fromUser: { id: Number(user?.id), name: user?.name || "Eu", profilePicUrl: user?.profilePicUrl || null },
				...(type === "direct" ? { toUserId: targetId } : { groupId: targetId }),
			};
			setMensagens((m) => [...m, temporaria]);
			tocarSomEnviadoChatInterno();

			let endpoint = "";
			if (type === "direct") endpoint = `/internal-chat/direct/${targetId}/messages`;
			else if (type === "group") endpoint = `/internal-chat/group/${targetId}/messages`;
			const { data } = await api.post(endpoint, form, { headers: { "Content-Type": "multipart/form-data" } });
			const enviadas = Array.isArray(data) ? data : [data];
			setMensagens((m) => {
				const semTemp = m.filter((x) => x.__tempId !== tempId);
				return [...semTemp, ...enviadas];
			});
			setTexto("");
			setAnexos((lista) => {
				lista.forEach((a) => { if (a.preview) try { URL.revokeObjectURL(a.preview); } catch {} });
				return [];
			});
		} catch (err) {
			toastError?.(err);
		} finally {
			setEnviando(false);
			requestAnimationFrame(() => inputRef.current?.focus?.());
		}
	};

	const toggleReaction = async (messageId, emoji) => {
		if (!messageId || !emoji) return;
		try {
			setReactionPopoverFor(null);
			await api.post(`/internal-chat/messages/${messageId}/reactions`, { emoji });
		} catch (err) {
			toastError?.(err);
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			enviarMensagem();
		}
	};

	const toggleBusca = () => {
		setBuscaAberta((antes) => {
			const abrir = !antes;
			setBuscaMsg("");
			setIndiceMatch(0);
			return abrir;
		});
	};
	const navegarMatch = (delta) => {
		if (!matches.length) return;
		setIndiceMatch((anterior) => {
			const prox = (anterior + delta + matches.length) % matches.length;
			const alvo = matches[prox];
			if (alvo) scrollParaMsgPorId(alvo.id);
			return prox;
		});
	};

	const abrirIA = () => {
		setAiVersoes([]);
		setAiSugestoes([]);
		setAiErro("");
		const digitado = String(texto || "").trim();
		setAiModo(digitado ? "corrigir" : "sugerir");
		setAiAberto(true);
		if (digitado) aiGerarVersao("corrigir", "Corrigido");
		else aiBuscarSugestoes();
	};

	const aiTrocarModo = (novo) => {
		setAiModo(novo);
		setAiErro("");
		if (novo === "sugerir" && !aiSugestoes.length) aiBuscarSugestoes();
		if (novo === "corrigir" && !aiVersoes.length) aiGerarVersao("corrigir", "Corrigido");
	};

	const aiGerarVersao = async (acao, rotulo) => {
		if (aiCarregando) return;
		setAiCarregando(acao);
		setAiErro("");
		try {
			const digitado = String(texto || "").trim();
			const { data } = await api.post("/ai/improve", { text: digitado, action: acao });
			const resultado = String(data?.texto || "").trim();
			if (!resultado) { setAiErro("A IA não devolveu texto."); return; }
			setAiVersoes((v) => [...v.filter((x) => x.acao !== acao), { acao, rotulo, texto: resultado }]);
		} catch (err) {
			setAiErro("A IA não devolveu uma correção utilizável. Verifique Talk.AI.");
			toastError?.(err);
		} finally { setAiCarregando(""); }
	};

	const aiBuscarSugestoes = async () => {
		if (aiCarregando) return;
		setAiCarregando("sugestoes");
		setAiErro("");
		try {
			const ultimas = mensagens.slice(-20);
			const meuNome = String(user?.name || "Eu");
			const nomeOutro = String(conversaInfo?.name || (type === "group" ? "Equipe" : "Usuário"));
			const linhas = ultimas.map((m) => {
				const autor = Number(m.fromUserId) === Number(user?.id) ? meuNome : (m.fromUser?.name || nomeOutro);
				const data = new Date(m.createdAt || Date.now()).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
				return `- ${autor} [${data}]: ${m.body || "(mídia: " + (m.mediaType || "arquivo") + ")"}`;
			}).join("\n");
			const prompt = `Sugira 3 respostas naturais para o chat interno abaixo (pt-BR).\n\nConversa:\n${linhas}\n\nEu sou "${meuNome}". Responda com JSON {"sugestoes":["resp1","resp2","resp3"]}. Se não der JSON, liste 3 linhas.`;
			const { data } = await api.post("/ai/improve", { text: prompt, action: "melhorar", json: true });
			const bruto = String(data?.texto || "").trim();
			let lista = [];
			try {
				const json = JSON.parse(bruto);
				if (Array.isArray(json?.sugestoes)) lista = json.sugestoes;
				else if (Array.isArray(json)) lista = json;
			} catch {}
			if (!lista.length) {
				lista = bruto.split(/\n+/).map((l) => l.replace(/^\s*[-*•0-9.)]+\s*/, "").trim()).filter(Boolean).slice(0, 3);
			}
			if (!lista.length) { setAiErro("A IA não devolveu sugestões. Tente de novo."); return; }
			setAiSugestoes(lista);
		} catch (err) {
			setAiErro("Não foi possível buscar sugestões. Verifique Talk.AI.");
			toastError?.(err);
		} finally { setAiCarregando(""); }
	};

	const aiUsar = (t) => {
		setTexto(String(t || ""));
		requestAnimationFrame(() => { inputRef.current?.focus?.(); });
		setAiAberto(false);
	};

	// ===== RENDER =====
	if (!parsed) {
		return (
			<div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-muted/30 text-center">
				<p className="text-sm text-muted-foreground">Link de conversa interna inválido.</p>
			</div>
		);
	}

	if (erro && !conversaInfo) {
		return (
			<div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-muted/30 text-center">
				<p className="text-sm text-muted-foreground">{erro}</p>
			</div>
		);
	}

	const euId = Number(user?.id);
	const fotoPerfil = conversaInfo?.profilePicUrl ? getPublicFileUrl(conversaInfo.profilePicUrl) : null;
	const nomeConversa = conversaInfo?.name || (type === "group" ? "Grupo" : "Conversa");
	const statusConversa = conversaInfo?.status;
	const ehGrupo = type === "group";

	return (
		<div className="flex h-full w-full min-w-0 flex-col overflow-hidden bg-muted/30" style={{ backgroundColor: darkMode ? "#111b21" : "#efeae2" }}>
			{/* ===== HEADER ===== */}
			<div className="flex shrink-0 items-center gap-3 border-r-0 px-4 py-2.5" style={{ backgroundColor: tema.bgHeader, borderBottom: `1px solid ${tema.borderSeparator}` }}>
				<Avatar className="h-10 w-10 shrink-0">
					{fotoPerfil && <AvatarImage src={fotoPerfil} alt={nomeConversa} referrerPolicy="no-referrer" />}
					<AvatarFallback style={{ backgroundColor: ehGrupo ? tema.avatarBgGrupo : tema.avatarBgPessoa, color: tema.avatarIconColor }}>
						{ehGrupo ? <Users className="h-4 w-4" /> : getInitials(nomeConversa)}
					</AvatarFallback>
				</Avatar>
				<div className="min-w-0 flex-1">
					<p className="truncate text-sm font-semibold" style={{ color: darkMode ? "#e9edef" : "#111b21" }}>{nomeConversa}</p>
					<p className="truncate text-xs" style={{ color: tema.textoMuted }}>
						{ehGrupo
							? "Chat interno da equipe"
							: (statusConversa ? (
								<>
									<span className="mr-1 inline-block h-2 w-2 rounded-full" style={{ backgroundColor: corPontoPresenca(statusConversa) }} />
									{rotuloPresenca(statusConversa)}
								</>
							) : "Chat interno")}
					</p>
				</div>
				<Button variant="ghost" size="icon" onClick={toggleBusca} title="Buscar mensagem">
					<Search className="h-4 w-4" />
				</Button>
			</div>

			{buscaAberta && (
				<div className="flex shrink-0 items-center gap-2 border-b px-4 py-2" style={{ backgroundColor: tema.bgInput, borderColor: tema.borderSeparator }}>
					<Input
						placeholder="Buscar nas mensagens..."
						value={buscaMsg}
						onChange={(e) => { setBuscaMsg(e.target.value); setIndiceMatch(0); }}
						className="h-8"
						autoFocus
					/>
					<div className="flex items-center gap-1 text-xs" style={{ color: tema.textoMuted }}>
						<Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navegarMatch(-1)} disabled={!matches.length}><ChevronUp className="h-4 w-4" /></Button>
						<span className="min-w-[48px] text-center">{matches.length ? `${indiceMatch + 1} / ${matches.length}` : "0 / 0"}</span>
						<Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navegarMatch(1)} disabled={!matches.length}><ChevronDown className="h-4 w-4" /></Button>
					</div>
				</div>
			)}

			{/* ===== MENSAGENS ===== */}
			<div
				ref={msgsScrollRef}
				className="relative flex-1 overflow-y-auto px-4 py-4"
				style={{
					backgroundColor: tema.bgChat,
					backgroundImage: tema.bgChatDots,
					backgroundRepeat: "repeat",
				}}
				onPaste={handlePaste}
			>
				{carregandoMsgs ? (
					<div className="flex h-full items-center justify-center">
						<Loader2 className="h-6 w-6 animate-spin" style={{ color: tema.textoMuted }} />
					</div>
				) : !mensagens.length ? (
					<div className="flex h-full items-center justify-center">
						<p className="rounded-full px-4 py-2 text-xs" style={{ backgroundColor: darkMode ? "#182229" : "#ffffff", color: tema.textoMuted }}>
							Nenhuma mensagem ainda. Envie um alô! 👋
						</p>
					</div>
				) : (
					<ul className="mx-auto flex max-w-[900px] flex-col gap-1">
						{mensagens.map((m) => {
							const fromMe = Number(m.fromUserId) === euId;
							const horario = m.createdAt
								? new Date(m.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
								: "";
							const balaoBg = fromMe ? tema.balaoEnviada : tema.balaoRecebida;
							const corTexto = fromMe ? tema.corTextoEnviada : tema.corTextoRecebida;
							const corTime = fromMe ? tema.corTimeEnviada : tema.corTimeRecebida;

							// Reações agregadas por emoji
							const reacoesRaw = Array.isArray(m.reacoes) ? m.reacoes : [];
							const reacoesAgrupadas = [];
							const reacoesMap = new Map();
							reacoesRaw.forEach((r) => {
								if (!r?.emoji) return;
								const e = String(r.emoji);
								const uids = Array.isArray(r.userIds) ? r.userIds.map(String) : (r.userId != null ? [String(r.userId)] : []);
								const existente = reacoesMap.get(e);
								if (existente) {
									uids.forEach((u) => { if (!existente.userIds.includes(u)) existente.userIds.push(u); });
								} else {
									reacoesMap.set(e, { emoji: e, userIds: uids });
								}
							});
							reacoesMap.forEach((v) => reacoesAgrupadas.push(v));

							return (
								<li
									key={m.id || m.__tempId}
									data-msg-id={m.id || ""}
									className={cn("flex", fromMe ? "justify-end" : "justify-start")}
								>
									<div className="relative max-w-[75%] sm:max-w-[65%]">
										<div
											className="rounded-[7.5px] px-2.5 py-1.5 shadow-sm"
											style={{ backgroundColor: balaoBg, color: corTexto }}
										>
											{!fromMe && ehGrupo && m.fromUser?.name && (
												<p className="mb-0.5 text-[13px] font-semibold" style={{ color: tema.corTimeRecebida }}>
													{String(m.fromUser.name)}
												</p>
											)}
											<RenderizarMidia
												mensagem={m}
												tema={tema}
												darkMode={darkMode}
												onAbrirImagem={(u, n) => setImagemModal({ url: u, nome: n })}
												abrirArquivo={abrirArquivo}
											/>
											{m.body && (
												<p className="whitespace-pre-wrap break-words text-[14.5px] leading-snug">
													{m.body}
												</p>
											)}
											<div className="mt-0.5 flex items-center justify-end gap-1.5">
												<span className="text-[10px] leading-none" style={{ color: corTime }}>
													{horario}
													{m.editedAt && <span className="ml-1 italic">(editado)</span>}
												</span>
											</div>
										</div>

										{/* Reações embaixo do balão */}
										{reacoesAgrupadas.length > 0 && (
											<div className={cn("mt-0.5 flex gap-1", fromMe ? "justify-end" : "justify-start")}>
												{reacoesAgrupadas.map((r) => {
													const euReagi = r.userIds.includes(String(euId));
													const bg = fromMe
														? (euReagi ? tema.bgChipReactionEuMinha : tema.bgChipReactionMinha)
														: (euReagi ? tema.bgChipReactionEu : tema.bgChipReaction);
													const borda = fromMe ? tema.balaoEnviada : tema.balaoRecebida;
													const corTextoChip = fromMe ? tema.corTextoEnviada : tema.corTextoRecebida;
													return (
														<Popover key={r.emoji}>
															<PopoverTrigger asChild>
																<button
																	type="button"
																	className="flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]"
																	style={{
																		backgroundColor: bg,
																		borderColor: borda,
																		color: corTextoChip,
																	}}
																>
																	<span>{r.emoji}</span>
																	{r.userIds.length > 1 && (
																		<span className="text-[10px] font-medium" style={{ color: tema.textoMuted }}>
																			{r.userIds.length}
																		</span>
																	)}
																</button>
															</PopoverTrigger>
															<PopoverContent side="top" align={fromMe ? "end" : "start"} className="w-auto text-xs">
																<div className="max-w-[200px] truncate">
																	{r.userIds.length === 1 ? "1 pessoa reagiu" : `${r.userIds.length} pessoas reagiram`}
																</div>
															</PopoverContent>
														</Popover>
													);
												})}
											</div>
										)}

										{/* Popover reação por hover/botão (simples: clique no canto) */}
										<div className={cn("absolute top-1/2 -translate-y-1/2 opacity-0 transition-opacity hover:opacity-100", fromMe ? "-left-9" : "-right-9")}>
											<Popover open={reactionPopoverFor === m.id} onOpenChange={(o) => setReactionPopoverFor(o ? m.id : null)}>
												<PopoverTrigger asChild>
													<Button variant="ghost" size="icon" className="h-7 w-7 rounded-full" title="Reagir">
														<Smile className="h-4 w-4" style={{ color: tema.textoMuted }} />
													</Button>
												</PopoverTrigger>
												<PopoverContent side={fromMe ? "left" : "right"} className="w-auto p-2">
													<div className="flex gap-1">
														{EMOJIS_RAPIDOS.map((em) => (
															<Button
																key={em}
																variant="ghost"
																size="icon"
																className="h-8 w-8 hover:bg-muted"
																onClick={() => toggleReaction(m.id, em)}
															>
																<span className="text-lg">{em}</span>
															</Button>
														))}
													</div>
													<div className="mt-1 border-t pt-1">
														<Popover>
															<PopoverTrigger asChild>
																<Button variant="ghost" size="sm" className="h-7 w-full text-xs">Mais emojis...</Button>
															</PopoverTrigger>
															<PopoverContent side={fromMe ? "left" : "right"} className="w-auto border-0 bg-transparent p-0 shadow-none">
																<Picker
																	data={emojiData}
																	locale="pt"
																	theme={darkMode ? "dark" : "light"}
																	onEmojiSelect={(sel) => {
																		setReactionPopoverFor(null);
																		toggleReaction(m.id, sel.native);
																	}}
																	maxFrequentRows={2}
																	navPosition="bottom"
																	previewPosition="none"
																	skinTonePosition="none"
																/>
															</PopoverContent>
														</Popover>
													</div>
												</PopoverContent>
											</Popover>
										</div>
									</div>
								</li>
							);
						})}
						<div ref={msgsEndRef} />
					</ul>
				)}
			</div>

			{/* ===== ANEXOS PREVIEW ===== */}
			{anexos.length > 0 && (
				<div className="flex shrink-0 flex-wrap gap-2 border-t px-4 py-2" style={{ backgroundColor: tema.bgInput, borderColor: tema.borderSeparator }}>
					{anexos.map((a, i) => {
						const nome = a.file.name || "arquivo";
						const tipo = a.file.type || tipoDeMidia(nome);
						return (
							<div key={i} className="relative flex items-center gap-2 rounded-lg border px-2 py-1.5" style={{ backgroundColor: tema.chipAnexoBg, borderColor: tema.borderSeparator }}>
								{a.preview ? (
									<img src={a.preview} alt={nome} className="h-12 w-12 rounded object-cover" />
								) : (
									<div className="flex h-12 w-12 items-center justify-center rounded" style={{ backgroundColor: tema.bgInput }}>
										{iconeDoAnexo(tipo, "h-5 w-5")}
									</div>
								)}
								<div className="min-w-0 max-w-[160px]">
									<p className="truncate text-xs font-medium">{nome}</p>
									<p className="text-[10px] text-muted-foreground">{(a.file.size / 1024).toFixed(1)} KB</p>
								</div>
								<button type="button" onClick={() => removerAnexo(i)} className="ml-1 flex h-5 w-5 items-center justify-center rounded-full hover:bg-muted">
									<X className="h-3 w-3" />
								</button>
							</div>
						);
					})}
				</div>
			)}

			{/* ===== INPUT ===== */}
			{gravando ? (
				<div className="flex shrink-0 items-center gap-3 border-t px-4 py-2.5" style={{ backgroundColor: tema.bgInput, borderColor: tema.borderSeparator }}>
					<RecordingTimer />
					<div className="flex-1 text-sm font-medium text-red-500">Gravando áudio...</div>
					<Button variant="ghost" size="icon" onClick={handleCancelAudio} title="Cancelar"><X className="h-5 w-5 text-red-500" /></Button>
					<Button variant="ghost" size="icon" onClick={handleUploadAudio} title="Enviar áudio"><Send className="h-5 w-5 text-green-600" /></Button>
				</div>
			) : (
				<div className="flex shrink-0 items-end gap-2 border-t px-3 py-2.5" style={{ backgroundColor: tema.bgInput, borderColor: tema.borderSeparator }}>
					<input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => adicionarAnexos(e.target.files)} />
					<Popover>
						<PopoverTrigger asChild>
							<Button variant="ghost" size="icon" className="shrink-0" title="Anexar">
								<Paperclip className="h-5 w-5" style={{ color: tema.textoMuted }} />
							</Button>
						</PopoverTrigger>
						<PopoverContent side="top" align="start" className="w-auto p-2">
							<div className="flex flex-col gap-1">
								<Button variant="ghost" size="sm" className="justify-start gap-2" onClick={() => { fileRef.current?.click(); }}>
									<Paperclip className="h-4 w-4" /> Arquivo
								</Button>
								<Button variant="ghost" size="sm" className="justify-start gap-2" onClick={() => { const i = fileRef.current; if (i) { i.accept = "image/*"; i.click(); i.accept = ""; } }}>
									<FileImage className="h-4 w-4" /> Imagem
								</Button>
							</div>
						</PopoverContent>
					</Popover>
					<Button
						variant="ghost"
						size="icon"
						className="shrink-0"
						title="Figurinhas"
						aria-label="Figurinhas"
						onClick={() => setFigurinhaAberto((a) => !a)}
						ref={figurinhaAncoraRef}
					>
						<Sticker className="h-5 w-5" style={{ color: figurinhaAberto ? tema.avatarIconColor : tema.textoMuted }} />
					</Button>
					<Input
						ref={inputRef}
						value={texto}
						onChange={(e) => setTexto(e.target.value)}
						onKeyDown={handleKeyDown}
						onPaste={handlePaste}
						placeholder="Digite uma mensagem..."
						className="min-h-[40px] resize-none border-0 shadow-none focus-visible:ring-0"
						style={{ backgroundColor: tema.inputBg, color: tema.corTextoRecebida, borderRadius: 8 }}
					/>
					<Button
						variant="ghost"
						size="icon"
						className="shrink-0"
						title="Inteligência Artificial (Talk.AI)"
						onClick={abrirIA}
						disabled={enviando || gravando}
					>
						<Sparkles className="h-5 w-5" style={{ color: tema.avatarIconColor }} />
					</Button>
					<Button variant="ghost" size="icon" className="shrink-0" title="Gravar áudio" onMouseDown={handleStartRecord}>
						<Mic className="h-5 w-5" style={{ color: tema.textoMuted }} />
					</Button>
					<Button
						variant="ghost"
						size="icon"
						className="shrink-0"
						disabled={enviando || (!texto.trim() && anexos.length === 0)}
						onClick={enviarMensagem}
						title="Enviar"
					>
						{enviando ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" style={{ color: tema.avatarIconColor }} />}
					</Button>
				</div>
			)}

			{/* ===== MODAIS ===== */}

			{/* ===== DIALOG IA COPILOTO (Talk.AI) ===== */}
			<Dialog
				open={aiAberto}
				onOpenChange={(v) => { if (aiCarregando === "") setAiAberto(v); }}
			>
				<DialogContent className="sm:max-w-[560px]">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<Sparkles className="h-5 w-5 text-primary" />
							Inteligência Artificial (Talk.AI)
						</DialogTitle>
					</DialogHeader>

					<div className="flex gap-2">
						<Button
							type="button"
							variant={aiModo === "corrigir" ? "default" : "secondary"}
							size="sm"
							onClick={() => aiTrocarModo("corrigir")}
							className="flex-1"
						>
							<SpellCheck className="mr-2 h-4 w-4" />
							Corrigir meu texto
						</Button>
						<Button
							type="button"
							variant={aiModo === "sugerir" ? "default" : "secondary"}
							size="sm"
							onClick={() => aiTrocarModo("sugerir")}
							className="flex-1"
						>
							<MessageSquare className="mr-2 h-4 w-4" />
							Sugerir resposta
						</Button>
					</div>

					{aiErro && (
						<div className="mt-3 rounded-md border border-destructive/60 bg-destructive/10 px-3 py-2 text-xs text-destructive">
							{aiErro}
						</div>
					)}

					<div className="mt-2 max-h-[60vh] space-y-3 overflow-y-auto pr-1">
						{aiModo === "corrigir" && (
							<>
								<div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
									{TONS_IA.map((t) => {
										const gerada = aiVersoes.find((v) => v.acao === t.acao);
										const carregandoAcao = aiCarregando === t.acao;
										return (
											<Button
												key={t.acao}
												type="button"
												variant="outline"
												size="sm"
												disabled={!!aiCarregando || !String(texto || "").trim()}
												onClick={() => aiGerarVersao(t.acao, t.rotulo)}
												className="text-xs"
											>
												{carregandoAcao ? (
													<Loader2 className="mr-2 h-3 w-3 animate-spin" />
												) : null}
												{t.rotulo}
											</Button>
										);
									})}
								</div>

								{!aiVersoes.length && aiCarregando && aiCarregando !== "sugestoes" && (
									<div className="rounded-lg border bg-muted/30 p-4 text-center">
										<Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
										<p className="mt-1 text-xs text-muted-foreground">Gerando versão...</p>
									</div>
								)}

								<div className="space-y-2">
									{aiVersoes.map((v) => (
										<button
											key={v.acao}
											type="button"
											onClick={() => aiUsar(v.texto)}
											className="group w-full rounded-lg border bg-card p-3 text-left transition-colors hover:border-primary/50 hover:bg-accent"
										>
											<div className="flex items-center justify-between gap-2 pb-1">
												<span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{v.rotulo}</span>
												<span className="flex items-center gap-1 text-[11px] text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
													<Check className="h-3 w-3" /> usar
												</span>
											</div>
											<p className="whitespace-pre-wrap text-sm leading-relaxed">{v.texto}</p>
										</button>
									))}
								</div>
							</>
						)}

						{aiModo === "sugerir" && (
							<>
								{aiCarregando === "sugestoes" && (
									<div className="rounded-lg border bg-muted/30 p-4 text-center">
										<Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
										<p className="mt-1 text-xs text-muted-foreground">Buscando sugestões com base na conversa...</p>
									</div>
								)}

								<div className="space-y-2">
									{aiSugestoes.map((s, i) => (
										<button
											key={i}
											type="button"
											onClick={() => aiUsar(s)}
											className="group w-full rounded-lg border bg-card p-3 text-left transition-colors hover:border-primary/50 hover:bg-accent"
										>
											<div className="flex items-center justify-between gap-2 pb-1">
												<span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Sugestão {i + 1}</span>
												<span className="flex items-center gap-1 text-[11px] text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100">
													<Check className="h-3 w-3" /> usar
												</span>
											</div>
											<p className="whitespace-pre-wrap text-sm leading-relaxed">{s}</p>
										</button>
									))}
								</div>

								{!aiCarregando && aiSugestoes.length > 0 && (
									<Button
										type="button"
										variant="secondary"
										size="sm"
										onClick={() => aiBuscarSugestoes()}
										className="w-full"
									>
										{aiSugestoes.length ? "Gerar outras" : "Tentar de novo"}
									</Button>
								)}
							</>
						)}
					</div>
				</DialogContent>
			</Dialog>

			{imagemModal && (
				<VisualizadorDeArquivo
					open={!!imagemModal}
					arquivos={[{ url: imagemModal.url, legenda: imagemModal.nome }]}
					indice={0}
					onClose={() => setImagemModal(null)}
					onTrocar={() => {}}
				/>
			)}
			{visualizando != null && (
				<VisualizadorDeArquivo
					open={visualizando != null}
					arquivos={arquivosDaConversa}
					indice={visualizando}
					onClose={() => setVisualizando(null)}
					onTrocar={(i) => setVisualizando(i)}
				/>
			)}

			{/* Figurinhas do InternalChat — posicionado com ancoraRef no botão Sticker.
			    Usa createPortal do FigurinhaPicker, então não importa a profundidade
			    da DOM aqui — ele sempre aparece sobre tudo, alinhado ao botão. */}
			<FigurinhaPicker
				aberto={figurinhaAberto && !!type && !!targetId}
				aoFechar={() => setFigurinhaAberto(false)}
				ticketId={null /* internal chat não usa rota ticket standard */}
				ancoraRef={figurinhaAncoraRef}
				quotedMsg={null /* InternalChatPanel ainda não tem responder/quoted; passar null é seguro */}
				customOnSelectSticker={(figurinha, ctx) =>
					enviarFigurinhaChatInterno(figurinha, ctx)
				}
			/>
		</div>
	);
};

export default InternalChatPanel;
